// Vendor libraries
import { catchError, map } from 'rxjs/operators';
import { NgZone } from '@angular/core';
import { of } from 'rxjs';
import Sortable from 'sortablejs';
import { Store } from '@ngrx/store';
import uuid from 'uuid-random';
// Constants / utils
import { disablePointerEvents, enablePointerEvents } from 'NewAngular//utils/sortable-utils';
import { hasValidReportSecurities } from 'CustomPDFReportsApp/ts/utils/custom-pdf-report-utils';
import { MULTI_SECURITY_COMPARISON_ADDITIONAL_SECURITY_COUNT_MAX, MULTI_SECURITY_COMPARISON_ADDITIONAL_SECURITY_COUNT_MIN, NAMING_CONVENTION_PREFERENCE_KEY, } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/constants';
import { CustomPDFReportType } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-type.enum';
import { ReportNamingConvention } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/enums/naming-convention.enum';
import { CustomPDFReportRequiredNumSecurities } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-required-num-securities.enum';
// Components
import { SavedAssetSecuritiesChoice } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/enums/custom-pdf-report-saved-asset-securities-choice.enum';
import { ReportSecuritySearchInputComponent } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/components/report-security-search-modal/report-security-search-input.component';
// Services
import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { NamingConventionService } from 'CustomPDFReportsApp/ts/services/naming-convention.service';
// NgRx actions
import { setCompTableSecuritiesChoice, setFundChartSecuritiesChoice, setScatterPlotSecuritiesChoice, setTimeseriesTableSecuritiesChoice, } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/state/saved-asset-securities-choices/saved-asset-securities-choices.action';
import { setPrimarySecurity, setSecondarySecurity, } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/state/report-securities/report-securities.actions';
import * as i0 from "@angular/core";
import * as i1 from "AccountsApp/ts/services/accounts.service";
import * as i2 from "@ngrx/store";
const _c0 = ["primarySecurityInput"];
const _c1 = ["secondarySecurityInput"];
const REPORT_NAMING_CONVENTION_OPTIONS = [
    {
        label: 'Truncated',
        value: ReportNamingConvention.TRUNCATED,
    },
    {
        label: 'Lettering System',
        value: ReportNamingConvention.LETTERING,
    },
    {
        label: 'Security Tickers/Identifiers',
        value: ReportNamingConvention.TICKER,
    },
];
const REPORT_NAMING_CONVENTION_OPTIONS_WITH_CUSTOM = [
    ...REPORT_NAMING_CONVENTION_OPTIONS,
    {
        label: 'Custom',
        value: ReportNamingConvention.CUSTOM,
    },
];
/**
 * Report Security Search Modal Directive
 *
 * Holds common properties and logic for modals that handle searching for and
 * selecting PDF report securities. Meant to be used with report-security-search-modal.template.html.
 */
export class ReportSecuritySearchModalDirective {
    constructor(accountsService, ngZone, store) {
        this.accountsService = accountsService;
        this.ngZone = ngZone;
        this.store = store;
        this.minSecurities = MULTI_SECURITY_COMPARISON_ADDITIONAL_SECURITY_COUNT_MIN;
        this.maxSecurities = MULTI_SECURITY_COMPARISON_ADDITIONAL_SECURITY_COUNT_MAX;
        this.autocompleterSearchType = 'report_securities';
        this.namingConvention = ReportNamingConvention.TRUNCATED;
        this.primarySecurity = {
            securityId: null,
            securityName: null,
            displaySecurityID: null,
            securityType: null,
            disableEditBtn: false,
            errorMessage: '',
        };
        this.secondarySecurity = {
            securityId: null,
            securityName: null,
            displaySecurityID: null,
            securityType: null,
            disableEditBtn: false,
            errorMessage: '',
        };
        this.comparisonSecurities = [];
        this.additionalSecurityInputCount = 0;
        this.securityLabelPreviews = [];
        this.isOpen = false;
        this.showSortable = true;
        this.sortableInstance = null;
        this.hidePrimarySecuritySearch = false;
        this.isAlive = true;
        this.buttonText = '';
        this.headerText = '';
        this.instructionTextByReportType = {
            [CustomPDFReportType.AGGREGATE_SECURITY_OVERVIEW]: 'Select a security to populate the report or select a list.',
            [CustomPDFReportType.AGGREGATE_SECURITY_COMPARISON]: 'Select 2 securities to populate the report or select a list.',
            [CustomPDFReportType.MULTI_SECURITY_COMPARISON]: 'Select at least three (3) securities up to six (6) securities to populate the report.',
        };
        // Use a unique ID in case there are multiple instances of components
        // that extend this directive on the same page.
        this.sortableContainerId = `comparison-securities-sortable-container-${uuid()}`;
        this.forwardDisabled = true;
        this.showAutocomplete = true;
        this.currentWizardStepIndex = null;
        this.currentWizardStep = null;
        this.REPORT_TYPES = CustomPDFReportType;
        this.selectedReportType = null;
        this.saveAssetSecuritiesChoiceActions = {
            fund_chart_securities_choice: setFundChartSecuritiesChoice,
            scatter_plot_securities_choice: setScatterPlotSecuritiesChoice,
            comp_table_securities_choice: setCompTableSecuritiesChoice,
            timeseries_table_securities_choice: setTimeseriesTableSecuritiesChoice,
        };
        this.savedAssetSecuritiesSelections = {
            fund_chart_securities_choice: SavedAssetSecuritiesChoice.SAVED_ASSET_SECURITIES_ONLY,
            scatter_plot_securities_choice: SavedAssetSecuritiesChoice.SAVED_ASSET_SECURITIES_ONLY,
            comp_table_securities_choice: SavedAssetSecuritiesChoice.SAVED_ASSET_SECURITIES_ONLY,
            timeseries_table_securities_choice: SavedAssetSecuritiesChoice.SAVED_ASSET_SECURITIES_ONLY,
        };
        this.savedAssetSecuritiesFields = [];
        this.showAdvancedOptions = false;
        this.namingConventionDropdownConfig = {
            list: REPORT_NAMING_CONVENTION_OPTIONS,
        };
        this.namingConventionFeature = false;
        this.allowSecurityLists = false;
        this.message = {
            message_text: '',
            message_type: null,
        };
        this.isMultiSecurityReport = false;
        this.loading = false;
        this.securitiesSubmitError = false;
        this.efficientFrontierModuleUrl = '';
        this.requiredNumSecurities = null;
    }
    ngOnInit() {
        this.accountsService.getClientGroupPreferences().subscribe((clientGroupPreference) => {
            const { allow_pdf_custom_naming } = clientGroupPreference;
            if (allow_pdf_custom_naming)
                this.namingConventionDropdownConfig = { list: REPORT_NAMING_CONVENTION_OPTIONS_WITH_CUSTOM };
        });
    }
    initializeSortable() {
        this.ngZone.runOutsideAngular(() => {
            if (this.sortableInstance) {
                this.sortableInstance.destroy();
                this.sortableInstance = null;
            }
            this.sortableInstance = Sortable.create(document.getElementById(this.sortableContainerId), {
                handle: '.drag-handle',
                scroll: false,
                animation: 150,
                draggable: '.draggable-security',
                onUpdate: (event) => {
                    this.ngZone.run(() => {
                        const newComparisonSecurities = [...this.comparisonSecurities];
                        newComparisonSecurities.splice(event.newIndex, 0, newComparisonSecurities.splice(event.oldIndex, 1)[0]);
                        this.setComparisonSecurities(newComparisonSecurities);
                        // This is a workaround for a bug that is reproduced by adding two items,
                        // dragging the top item to the bottom, and then adding another item. The items
                        // then will be out of order and the sorting will be broken
                        this.showSortable = false;
                        setTimeout(() => {
                            this.showSortable = true;
                            setTimeout(() => {
                                this.initializeSortable();
                            });
                        });
                    });
                },
                onStart: () => {
                    disablePointerEvents(document, 'options-list-item');
                },
                onEnd: () => {
                    enablePointerEvents(document, 'options-list-item');
                },
            });
        });
    }
    closeModal() {
        this.isOpen = false;
    }
    openModal() {
        this.isOpen = true;
        switch (this.requiredNumSecurities) {
            case CustomPDFReportRequiredNumSecurities.ONE:
                this.additionalSecurityInputCount = 0;
                break;
            case CustomPDFReportRequiredNumSecurities.TWO:
                this.additionalSecurityInputCount = 0;
                if (this.comparisonSecurities.length) {
                    this.setSecondarySecurity(this.comparisonSecurities[0]);
                    this.setComparisonSecurities([]);
                }
                break;
            case CustomPDFReportRequiredNumSecurities.THREE_PLUS:
                if (this.secondarySecurity.securityId) {
                    this.setComparisonSecurities([this.secondarySecurity]);
                    this.setSecondarySecurity({
                        securityId: '',
                        securityName: '',
                        displaySecurityID: '',
                        securityType: '',
                        disableEditBtn: false,
                        errorMessage: '',
                    });
                }
                if (this.comparisonSecurities.length < 2) {
                    this.additionalSecurityInputCount = 2;
                }
                else {
                    this.additionalSecurityInputCount = this.comparisonSecurities.length;
                }
                break;
            default:
        }
    }
    validateSecurities() {
        this.forwardDisabled = !(hasValidReportSecurities(this.selectedReportType, this.primarySecurity, this.secondarySecurity, this.comparisonSecurities) &&
            this.validCustom() &&
            this.comparisonSecurities.every((sec) => sec.securityId));
        // Delay this so the autocompleter has time to unfocus before it is hidden
        setTimeout(() => {
            this.showAutocomplete = this.comparisonSecurities.length < this.maxSecurities;
        });
    }
    updateSavedAssetSecuritiesChoice(fieldChoice) {
        this.store.dispatch(this.saveAssetSecuritiesChoiceActions[fieldChoice.field]({ choice: fieldChoice.choice }));
    }
    updateAdvancedOptionsPreference(value) {
        this.accountsService
            .putUserPreferences({ show_advanced_report_generation_options: value })
            .subscribe({ error: () => { } });
    }
    interpretSecurityLabelPreview() {
        const securities = [this.primarySecurity, this.secondarySecurity, ...this.comparisonSecurities];
        this.securityLabelPreviews = securities.map((sec, idx) => {
            // we need to adjust the index because with comparison security we don't have a "secondary security"
            return NamingConventionService.reportLabel(sec, this.namingConvention, idx - (idx > 1 ? 1 : 0));
        });
    }
    createAdditionalSecurityInput() {
        // Verify that we can add more securities, and that all the existing additional inputs are utilized (no empty boxes)
        if (this.comparisonSecurities.length < this.maxSecurities &&
            this.additionalSecurityInputCount === this.comparisonSecurities.length) {
            this.additionalSecurityInputCount += 1;
        }
    }
    removeSecurityHandlerFac(index) {
        return () => this.removeSecurity(index);
    }
    getNamingConventionPreference() {
        const prevPref = localStorage.getItem(NAMING_CONVENTION_PREFERENCE_KEY);
        if (prevPref === 'lettering') {
            return of(ReportNamingConvention.LETTERING);
        }
        if (prevPref === 'ticker') {
            return of(ReportNamingConvention.TICKER);
        }
        if (prevPref === 'custom') {
            return this.accountsService.getClientGroupPreferences().pipe(map((clientGroupPreference) => {
                if (clientGroupPreference.allow_pdf_custom_naming) {
                    return ReportNamingConvention.CUSTOM;
                }
                return ReportNamingConvention.TRUNCATED;
            }), catchError(() => {
                return of(ReportNamingConvention.TRUNCATED);
            }));
        }
        return of(ReportNamingConvention.TRUNCATED);
    }
    validCustom() {
        if (this.namingConvention !== ReportNamingConvention.CUSTOM)
            return true;
        const securities = [this.primarySecurity, this.secondarySecurity, ...this.comparisonSecurities];
        return securities
            .map((security, index) => {
            return security.securityId ? index : null;
        })
            .filter((index) => index !== null)
            .every((index) => !this.fieldIsBlank(this.securityLabelPreviews[index]));
    }
    fieldIsBlank(fieldValue) {
        return fieldValue === null || fieldValue === undefined || String(fieldValue).trim() === '';
    }
    setErrorsInReportSecurities(securitiesErrors) {
        const securities = Object.keys(securitiesErrors);
        securities.forEach((security) => {
            if (this.primarySecurity.securityId === security) {
                this.primarySecurity = {
                    ...this.primarySecurity,
                    errorMessage: securitiesErrors[security],
                    disableEditBtn: true,
                };
                this.store.dispatch(setPrimarySecurity({
                    ...this.primarySecurity,
                }));
            }
            else if (this.secondarySecurity.securityId === security) {
                this.secondarySecurity = {
                    ...this.secondarySecurity,
                    errorMessage: securitiesErrors[security],
                    disableEditBtn: true,
                };
                this.store.dispatch(setSecondarySecurity({
                    ...this.secondarySecurity,
                }));
            }
            else {
                this.comparisonSecurities = this.comparisonSecurities.map((comparisonSecurity) => {
                    if (comparisonSecurity.securityId === security) {
                        return {
                            ...comparisonSecurity,
                            errorMessage: securitiesErrors[security],
                            disableEditBtn: true,
                        };
                    }
                    return { ...comparisonSecurity };
                });
                this.setComparisonSecurities(this.comparisonSecurities);
            }
        });
    }
    enableEditSecurities() {
        this.primarySecurity = {
            ...this.primarySecurity,
            disableEditBtn: false,
        };
        this.store.dispatch(setPrimarySecurity({
            ...this.primarySecurity,
        }));
        this.secondarySecurity = {
            ...this.secondarySecurity,
            disableEditBtn: false,
        };
        this.store.dispatch(setSecondarySecurity({
            ...this.secondarySecurity,
        }));
        this.comparisonSecurities = this.comparisonSecurities.map((comparisonSecurity) => {
            return {
                ...comparisonSecurity,
                disableEditBtn: false,
            };
        });
        this.setComparisonSecurities(this.comparisonSecurities);
        this.buttonText = 'Submit';
    }
}
ReportSecuritySearchModalDirective.ɵfac = function ReportSecuritySearchModalDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ReportSecuritySearchModalDirective)(i0.ɵɵdirectiveInject(i1.AccountsService), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i2.Store)); };
ReportSecuritySearchModalDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: ReportSecuritySearchModalDirective, viewQuery: function ReportSecuritySearchModalDirective_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 5);
        i0.ɵɵviewQuery(_c1, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.primarySecurityInput = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.secondarySecurityInput = _t.first);
    } } });
